import dayjs from 'dayjs'
import formatDuration from '../../../../helpers/date-time/format-duration'
import { getShipmentDisplayCalculated } from '../../../../helpers/entities/shipment/get-shipment-display-calculated'
import formatNumber from '../../../../helpers/formatters/format-any-number'
import MaterialConsumption from '../../../../types/api/material-consumption'
import PrintJob from '../../../../types/api/print-job'
import PrintJobExecutor, { Reasons } from '../../../../types/api/print-job-executor'
import {
    hasSavedMaterialConsumptions,
    parseAmountKm,
    parseLayerNumber,
    sumMaterialConsumptionLengthKm,
} from '../../../completed-print-jobs/helpers/consumption-helpers'
import {
    DCMQ_BONUS_COEFFICIENT,
    lookupGfLengthBonus,
    lookupVolumeBonusCoefficient,
    normalizeMachineBrandName,
    VolumeBonusBrand,
} from '../constants/printer-bonus-coefficients'

export interface PrinterMonthlyStatsRaw {
    timeDiffMinutes: number | null
    volume: number | null
    savings: number | null
    overuseDeduction: number | null
    bonus: number | null
    printJobsCount: number
}

export type MetricTone = 'success' | 'danger'

export interface PrinterMonthlyStatsFormatted {
    time: string | null
    timeTone: MetricTone | null
    volume: string | null
    savings: string | null
    savingsTone: MetricTone | null
    overuseDeduction: string | null
    bonus: string | null
    bonusTone: MetricTone | null
}

interface PrintJobMetrics {
    brand: VolumeBonusBrand | undefined
    volume: number
    savings: number
    factLengthKm: number
}

function getLayerOneConsumption(
    consumptions: MaterialConsumption[] | undefined
): MaterialConsumption | undefined {
    return consumptions?.find(item => parseLayerNumber(item.layerNumber) === 1)
}

function getActualLengthKm(consumptions: MaterialConsumption[] | undefined): number {
    const layerOneSum = sumMaterialConsumptionLengthKm(consumptions)

    if (layerOneSum > 0) {
        return layerOneSum
    }

    const firstConsumption = consumptions?.[0]

    return firstConsumption ? parseAmountKm(firstConsumption.amount) : 0
}

function getActualWidth(consumptions: MaterialConsumption[] | undefined): number | undefined {
    const layerOne = getLayerOneConsumption(consumptions)
    const width = layerOne?.material?.width ?? consumptions?.[0]?.material?.width

    if (width == null) {
        return undefined
    }

    const numeric = Number(width)

    return Number.isFinite(numeric) ? numeric : undefined
}

function calcPrintJobMetrics(printJob: PrintJob): Omit<PrintJobMetrics, 'brand'> | undefined {
    if (!hasSavedMaterialConsumptions(printJob)) {
        return undefined
    }

    const actualLen = getActualLengthKm(printJob.material_consumptions)
    const actualWidth = getActualWidth(printJob.material_consumptions)
    const calcLen = getShipmentDisplayCalculated(printJob.shipment)?.materialLength

    if (actualWidth == null || calcLen == null || actualLen <= 0) {
        return undefined
    }

    const cappedLen = Math.min(actualLen, calcLen)

    return {
        volume: cappedLen * actualWidth,
        savings: (calcLen - actualLen) * actualWidth,
        factLengthKm: actualLen,
    }
}

function calcOveruseDeduction(totalSavings: number): number {
    return totalSavings < 0 ? Math.abs(totalSavings) * 1.6 : totalSavings
}

function calcBrandBonus(
    brand: VolumeBonusBrand,
    brandVolume: number,
    brandFactLengthKm: number,
    brandOveruseDeduction: number
): number | null {
    switch (brand) {
        case 'Omet':
        case 'MPS':
        case 'Bobst':
        case 'Durst': {
            const coefficient = lookupVolumeBonusCoefficient(brand, brandVolume)

            if (coefficient == null) return null

            return coefficient * brandVolume - brandOveruseDeduction
        }
        case 'GF': {
            const fixedBonus = lookupGfLengthBonus(brandFactLengthKm)

            if (fixedBonus == null) return null

            return fixedBonus - brandOveruseDeduction
        }
        case 'DCMQ':
            return DCMQ_BONUS_COEFFICIENT * brandVolume - brandOveruseDeduction
        default:
            return null
    }
}

function roundMetric(value: number): number {
    return Math.round(value * 1000) / 1000
}

export function formatSignedDuration(totalDiffMinutes: number): string {
    const prefix = totalDiffMinutes <= 0 ? '+' : '-'

    return `${prefix}${formatDuration(Math.abs(totalDiffMinutes))}`
}

export function formatMetric(value: number): string {
    return formatNumber(roundMetric(value))
}

/** Время: факт ≤ план → зелёный; факт > план → красный. */
export function toneFromTimeDiff(timeDiffMinutes: number): MetricTone {
    return timeDiffMinutes <= 0 ? 'success' : 'danger'
}

/** Положительное → зелёный; отрицательное → красный; 0 → без цвета. */
export function toneFromSignedValue(value: number): MetricTone | null {
    if (value === 0) return null

    return value > 0 ? 'success' : 'danger'
}

export function calcPrinterMonthlyStatsRaw(executors: PrintJobExecutor[]): PrinterMonthlyStatsRaw {
    let timeDiffMinutes: number | null = null
    let hasTimeData = false
    let totalTimeDiff = 0

    const printJobsById = new Map<
        string,
        { printJob: PrintJob; brand: VolumeBonusBrand | undefined }
    >()

    for (const executor of executors) {
        const printJob = executor.print_job

        if (!printJob?.documentId) continue

        const brand = normalizeMachineBrandName(executor.machine?.machine_brand?.name)

        if (!printJobsById.has(printJob.documentId)) {
            printJobsById.set(printJob.documentId, { printJob, brand })
        }

        const start = executor.start
        const end = executor.end
        const calculated = getShipmentDisplayCalculated(printJob.shipment)?.commonTime

        if (
            executor.reason === Reasons.Done &&
            start &&
            end &&
            calculated != null
        ) {
            const actual = Math.max(0, dayjs(end).diff(dayjs(start), 'minute', true))
            totalTimeDiff += actual - calculated
            hasTimeData = true
        }
    }

    if (hasTimeData) {
        timeDiffMinutes = totalTimeDiff
    }

    const jobMetrics: PrintJobMetrics[] = []

    for (const { printJob, brand } of printJobsById.values()) {
        const metrics = calcPrintJobMetrics(printJob)

        if (!metrics) continue

        jobMetrics.push({ ...metrics, brand })
    }

    if (!jobMetrics.length) {
        return {
            timeDiffMinutes,
            volume: null,
            savings: null,
            overuseDeduction: null,
            bonus: null,
            printJobsCount: printJobsById.size,
        }
    }

    const totalVolume = jobMetrics.reduce((sum, item) => sum + item.volume, 0)
    const totalSavings = jobMetrics.reduce((sum, item) => sum + item.savings, 0)
    const overuseDeduction = calcOveruseDeduction(totalSavings)

    const brandGroups = new Map<
        VolumeBonusBrand,
        { volume: number; savings: number; factLengthKm: number }
    >()

    for (const item of jobMetrics) {
        if (!item.brand) continue

        const current = brandGroups.get(item.brand) ?? {
            volume: 0,
            savings: 0,
            factLengthKm: 0,
        }

        current.volume += item.volume
        current.savings += item.savings
        current.factLengthKm += item.factLengthKm
        brandGroups.set(item.brand, current)
    }

    let totalBonus = 0
    let hasBonus = false

    for (const [brand, group] of brandGroups) {
        const brandOveruseDeduction = calcOveruseDeduction(group.savings)
        const brandBonus = calcBrandBonus(
            brand,
            group.volume,
            group.factLengthKm,
            brandOveruseDeduction
        )

        if (brandBonus == null) continue

        totalBonus += brandBonus
        hasBonus = true
    }

    return {
        timeDiffMinutes,
        volume: totalVolume,
        savings: totalSavings,
        overuseDeduction,
        bonus: hasBonus ? totalBonus : null,
        printJobsCount: printJobsById.size,
    }
}

function formatPrinterMonthlyStats(
    raw: PrinterMonthlyStatsRaw
): PrinterMonthlyStatsFormatted {
    return {
        time:
            raw.timeDiffMinutes == null ? null : formatSignedDuration(raw.timeDiffMinutes),
        timeTone:
            raw.timeDiffMinutes == null ? null : toneFromTimeDiff(raw.timeDiffMinutes),
        volume: raw.volume == null ? null : formatMetric(raw.volume),
        savings: raw.savings == null ? null : formatMetric(raw.savings),
        savingsTone: raw.savings == null ? null : toneFromSignedValue(raw.savings),
        overuseDeduction:
            raw.overuseDeduction == null ? null : formatMetric(raw.overuseDeduction),
        bonus: raw.bonus == null ? null : formatMetric(raw.bonus),
        bonusTone: raw.bonus == null ? null : toneFromSignedValue(raw.bonus),
    }
}

export function calcPrinterMonthlyStats(
    executors: PrintJobExecutor[]
): PrinterMonthlyStatsFormatted {
    return formatPrinterMonthlyStats(calcPrinterMonthlyStatsRaw(executors))
}
